import { useEffect, useRef } from 'preact/hooks';
import type { Ref } from 'preact';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';
import { reconstructWrappedUrls, resolveFullUrl } from '../utils/sshUrlReconstruct';
import { stripAnsi } from '../utils/stripAnsi';

export interface SshTerminalHandle {
  scrollPages: (n: number) => void;
}

interface Props {
  /** Raw SSH output chunks — each new entry is written to the terminal */
  output: string[];
  /** Output epoch (Phase 4). Changes ⇒ `output` was REPLACED (commitConnect /
   *  reconcile / reset), not appended — the renderer resets xterm and re-sources
   *  from the new array. Same epoch ⇒ pure append, write only the delta. This is
   *  what retires the writtenRef append-only symptom-guard that mis-rendered P3's
   *  reconcile output-replace on a no-reload socket reconnect. */
  outputEpoch: number;
  /** Called when user types in the terminal (keystrokes) */
  onInput: (data: string) => void;
  /** Called when terminal resizes (cols, rows) */
  onResize?: (cols: number, rows: number) => void;
  /** Imperative handle ref for scroll control */
  handleRef?: Ref<SshTerminalHandle | null>;
  /** Hide terminal (keep alive) while showing chat/console in isSelf mode */
  hidden?: boolean;
}

export function SshTerminal({ output, outputEpoch, onInput, onResize, handleRef, hidden }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  /** In-epoch write cursor — how many chunks of the CURRENT epoch are already on
   *  the xterm buffer. Reset to 0 whenever the epoch changes (output replaced). */
  const writtenRef = useRef(0);
  /** The output epoch currently reflected on the xterm buffer. -1 = nothing
   *  rendered yet (fresh mount) so the first paint always re-sources. */
  const renderedEpochRef = useRef(-1);
  // ssh-terminal-fsm-migration Phase 2: the `output` prop is now sourced from the
  // authoritative sshTerminalStore (via useSocket's useSshOutput selector). This
  // component is a pure renderer of that store-backed prop.
  /** Reconstructed full URLs from recent output — used to expand truncated inline link clicks (#1356) */
  const linkUrlsRef = useRef<string[]>([]);

  // Initialize xterm.js once on mount
  useEffect(() => {
    if (!containerRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      fontSize: 13,
      fontFamily: "'SF Mono', 'Fira Code', 'Cascadia Code', 'Courier New', monospace",
      theme: {
        background: '#0a1a0a',
        foreground: '#c0e0c0',
        cursor: '#44aa44',
        selectionBackground: '#2a4a2a',
        black: '#0a1a0a',
        green: '#44cc88',
        brightGreen: '#69ff94',
        red: '#cc4444',
        brightRed: '#ff6e6e',
        yellow: '#ccaa33',
        brightYellow: '#ffffa5',
        blue: '#6666cc',
        brightBlue: '#d6acff',
        magenta: '#cc44cc',
        brightMagenta: '#ff92df',
        cyan: '#44aaaa',
        brightCyan: '#a4ffff',
        white: '#c0e0c0',
        brightWhite: '#ffffff',
        brightBlack: '#888888',
      },
      allowProposedApi: true,
      scrollback: 5000,
      convertEol: false,
      smoothScrollDuration: 80,
      scrollSensitivity: 3,
      fastScrollSensitivity: 8,
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    // Make URLs in terminal output tappable — opens in new tab.
    // resolveFullUrl expands truncated xterm line-matched URIs to the full
    // reconstructed URL from linkUrlsRef (#1356).
    term.loadAddon(new WebLinksAddon((_ev, uri) => {
      const full = resolveFullUrl(uri, linkUrlsRef.current);
      window.open(full, '_blank', 'noopener,noreferrer');
    }));
    term.open(containerRef.current);

    // Fit terminal to container size
    try { fitAddon.fit(); } catch { /* container might not be visible yet */ }

    // Forward user keystrokes to SSH PTY via onInput callback
    term.onData((data) => {
      onInput(data);
    });

    // Notify backend of terminal dimensions so SSH PTY can match
    term.onResize(({ cols, rows }) => {
      onResize?.(cols, rows);
    });

    // Re-fit on window resize
    const handleResize = () => {
      try { fitAddon.fit(); } catch { /* ignore */ }
    };
    window.addEventListener('resize', handleResize);

    termRef.current = term;
    fitRef.current = fitAddon;
    writtenRef.current = 0;

    // Expose scroll methods via handleRef
    if (handleRef && typeof handleRef === 'object') {
      (handleRef as any).current = {
        scrollPages: (n: number) => term.scrollPages(n),
      };
    }

    // Send initial size to backend
    onResize?.(term.cols, term.rows);

    return () => {
      window.removeEventListener('resize', handleResize);
      term.dispose();
      termRef.current = null;
      fitRef.current = null;
      writtenRef.current = 0;
      if (handleRef && typeof handleRef === 'object') {
        (handleRef as any).current = null;
      }
    };
  }, []); // Only run once on mount

  // Reconcile the xterm buffer to the store output (Phase 4 — epoch-aware).
  //   epoch changed  → output was REPLACED (commitConnect / reconcile / reset):
  //                    clear the terminal and re-source the whole new array. This
  //                    is the fix for P3's reconcile replace — the old append-only
  //                    writtenRef left a stale cursor and froze the terminal on a
  //                    no-reload socket reconnect.
  //   epoch same     → pure append: write only the un-written delta (the store's
  //                    seq-idempotent appendOutput guarantees no dup/reorder).
  useEffect(() => {
    const term = termRef.current;
    if (!term) return;

    if (outputEpoch !== renderedEpochRef.current) {
      term.reset();
      writtenRef.current = 0;
      renderedEpochRef.current = outputEpoch;
    }

    for (let i = writtenRef.current; i < output.length; i++) {
      term.write(output[i]);
    }
    writtenRef.current = output.length;

    // Keep the reconstructed full-URL list fresh so the inline link handler can
    // expand a hard-wrap-truncated click to the full URL (#1356).
    linkUrlsRef.current = reconstructWrappedUrls(stripAnsi(output.slice(-4000).join('')));
  }, [output, outputEpoch]);

  // Re-fit when container becomes visible (e.g. SSH connects or toggled back from chat)
  useEffect(() => {
    if (hidden) return;
    const timer = setTimeout(() => {
      try { fitRef.current?.fit(); } catch { /* ignore */ }
    }, 50);
    return () => clearTimeout(timer);
  }, [hidden]);

  return (
    <div
      class={`ssh-terminal xterm-container${hidden ? ' ssh-terminal-hidden' : ''}`}
      ref={containerRef}
    />
  );
}
